<script setup lang="ts">
import {computed} from "vue"
import {FluencyRecording, FluencyGrade, markupSelectedAndWpm, lessonTextForGrade} from "~app/fluency"
import {useVModels} from "@vueuse/core"
import {toggle_include} from "@/common/utils"
import {FluencyGraderState} from "~components/teacher/fluency/FluencyGrader.vue"

const props = defineProps<{
  reading: FluencyRecording
  grade: FluencyGrade
  state: FluencyGraderState
}>()

const emit = defineEmits([])

const {grade} = useVModels(props, emit)

const grading = computed(() => props.state === "grade")

const isBenchmark = computed(() => props.reading.classification === "benchmark")
const isColdRead = computed(() => props.reading.classification === "cold_read")
// const isHotRead = computed(() => props.reading.classification === "hot_read")
const errorClass = computed(() => (isBenchmark.value || isColdRead.value) ? "cold" : "hot")
const needsWPM = computed(() => props.reading.classification === "benchmark" || props.reading.lyrics_only) // FIXME

const lyricsHtml = computed(() => {
  const text = lessonTextForGrade(props.reading.lesson, grade.value)
  const selected = grade.value.selectedWordIds
  return markupSelectedAndWpm(text, errorClass.value, selected, needsWPM.value ? grade.value.wpm : 0)
})

function onClick(event: any) {
  if (!grading.value) return
  const parent = window.getSelection()?.anchorNode?.parentNode as HTMLElement
  const id = parent.getAttribute("l2l-id") ?? parent.getAttribute("l2l-word-id")
  if (!id) return
  console.log("click", id)

  if (event.metaKey || event.altKey) {
    const index = parent.getAttribute("l2l-id") ?? parent.getAttribute("l2l-word-index") ?? "0"
    console.log("wpm", index)
    if (needsWPM.value) grade.value.wpm = parseInt(index)
  } else {
    grade.value.selectedWordIds = toggle_include(grade.value.selectedWordIds, id)
    grade.value.accuracy = -grade.value.selectedWordIds.length
  }
}

function onContextMenu() {
  // no-op to disable context menu
}
</script>

<template>
  <div
    class="lyrics-wrapper"
    :class="{grading}"
    @click="onClick($event)"
    @contextmenu.prevent="onContextMenu"
  >
    <section v-if="grading" class="grading-instructions">
      <header>Grading Instructions</header>
      <ul>
        <li>
          Click on words missed to track accuracy. Do not click for self-corrections.
        </li>
        <li v-if="needsWPM">
          <b>PC:</b> Hold down the <kbd>Alt</kbd> or <kbd>🔎</kbd> key and click the last word read for total WPM.
        </li>
        <li v-if="needsWPM">
          <b>Mac:</b> Hold down the <kbd>⌥</kbd> or <kbd>⌘</kbd> key and click the last word read for total WPM.
        </li>
      </ul>
      <hr>
    </section>

    <div class="lyrics">
      <h1>{{ reading.lesson.title }}</h1>
      <div v-html="lyricsHtml"></div>
    </div>
  </div>
</template>

<style lang="scss">
@import "../../../../sass/theme";

$cold: rgba(0, 200, 200);
$hot: rgba(255, 0, 0);

.lyrics-wrapper {
  &.grading {
    .l2l-word, [l2l-word-id] {
      cursor: pointer;

      &:hover {
        background-color: #eee;

        &.cold, &[l2l-cold-error] {
          background-color: $cold;
        }

        &.hot, &[l2l-hot-error] {
          background-color: $hot;
        }
      }
    }
  }

  .wpm-marker, [l2l-wpm-marker] {
    border: 2px solid $red;
  }

  .cold, [l2l-cold-error] {
    background-color: transparentize($cold, 0.5);
  }

  .hot, [l2l-hot-error] {
    background-color: transparentize($hot, 0.5);
  }

  h1 {
    font-size: 20px;
    font-style: oblique;
    text-align: center;
    color: black;
  }

  p {
    margin: 0 0 1.3em;
    font-size: 1.375em;
  }

  .grading-instructions {
    font-size: small;

    header {
      font-size: medium;
      font-weight: bold;
      text-align: center;
    }

    ul {
      list-style-type: none;
      font-style: italic;

      li {
        margin-top: 0.5rem;
      }
    }
  }
}
</style>
